import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { getSystemErrors } from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp, FileText, User, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const ERROR_TYPE_LABELS = {
  ocr_error: { label: 'OCR Scan Error', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
};

function typeInfo(action) {
  return ERROR_TYPE_LABELS[action] || { label: action, color: 'var(--text-muted)', bg: 'rgba(255,255,255,0.04)' };
}

function timeAgo(ts) {
  if (!ts) return '—';
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function ErrorRow({ row, isMobile }) {
  const [expanded, setExpanded] = useState(false);
  const meta = row.metadata || {};
  const info = typeInfo(row.action);

  if (isMobile) {
    return (
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)', padding: '14px',
        marginBottom: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <span style={{
            fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px',
            borderRadius: 10, background: info.bg, color: info.color, whiteSpace: 'nowrap',
          }}>{info.label}</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>{timeAgo(row.created_at)}</span>
        </div>

        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#ef4444', marginBottom: 4 }}>
          {meta.error_message || 'Unknown error'}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginBottom: 8 }}>
          {meta.file_name && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <FileText size={11} /> {meta.file_name}
            </span>
          )}
          {meta.org_name && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Building2 size={11} /> {meta.org_name}
            </span>
          )}
          {meta.user_email && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <User size={11} /> {meta.user_email}
            </span>
          )}
        </div>

        {meta.error_detail && (
          <button
            onClick={() => setExpanded(p => !p)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.72rem', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {expanded ? 'Hide' : 'Show'} stack trace
          </button>
        )}
        {expanded && meta.error_detail && (
          <pre style={{
            marginTop: 8, padding: '8px 10px', borderRadius: 6,
            background: 'rgba(0,0,0,0.15)', fontSize: '0.68rem', color: '#ef4444',
            whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.5,
          }}>{meta.error_detail}</pre>
        )}
      </div>
    );
  }

  return (
    <>
      <tr style={{ borderBottom: '1px solid var(--border)', cursor: meta.error_detail ? 'pointer' : 'default' }}
          onClick={() => meta.error_detail && setExpanded(p => !p)}>
        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
          <span style={{
            fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px',
            borderRadius: 10, background: info.bg, color: info.color,
          }}>{info.label}</span>
        </td>
        <td style={{ padding: '10px 14px' }}>
          <div style={{ fontSize: '0.82rem', color: '#ef4444', fontWeight: 500 }}>
            {meta.error_message || 'Unknown error'}
          </div>
          {meta.file_name && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FileText size={10} /> {meta.file_name}
              {meta.file_type && <span style={{ opacity: 0.6 }}>({meta.file_type})</span>}
            </div>
          )}
        </td>
        <td style={{ padding: '10px 14px' }}>
          {meta.org_name ? (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Building2 size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              {meta.org_name}
            </div>
          ) : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>}
          {meta.user_email && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <User size={10} /> {meta.user_email}
            </div>
          )}
        </td>
        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)' }}>{formatDate(row.created_at)}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{timeAgo(row.created_at)}</div>
        </td>
        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
          {meta.error_detail
            ? <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center' }}>
                {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {expanded ? 'Hide' : 'Detail'}
              </span>
            : <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>}
        </td>
      </tr>
      {expanded && meta.error_detail && (
        <tr style={{ background: 'rgba(239,68,68,0.03)', borderBottom: '1px solid var(--border)' }}>
          <td colSpan={5} style={{ padding: '0 14px 12px 14px' }}>
            <pre style={{
              margin: 0, padding: '10px 12px', borderRadius: 6,
              background: 'rgba(0,0,0,0.15)', fontSize: '0.72rem', color: '#ef4444',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6,
            }}>{meta.error_detail}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SystemErrorsPage() {
  const isMobile = useIsMobile();
  const [errors,  setErrors]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSystemErrors({ limit: LIMIT, offset: page * LIMIT });
      setErrors(data.errors || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load system errors');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const hasPrev = page > 0;
  const hasNext = (page + 1) * LIMIT < total;

  return (
    <Layout title="System Errors" crumb={total > 0 ? `${total} error${total !== 1 ? 's' : ''}` : 'No errors'}>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={15} style={{ color: '#f59e0b' }} />
          <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Errors logged automatically from the CMS — click any row to see the stack trace.
          </span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /> Loading errors…</div>
      ) : errors.length === 0 ? (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <AlertTriangle size={32} style={{ color: '#22c55e', marginBottom: 12, opacity: 0.7 }} />
          <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>No errors logged</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            OCR failures and other CMS errors will appear here automatically.
          </div>
        </div>
      ) : isMobile ? (
        <div>
          {errors.map(row => <ErrorRow key={row.id} row={row} isMobile />)}
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Type</th>
                  <th>Error / File</th>
                  <th style={{ width: 200 }}>Org / User</th>
                  <th style={{ width: 170 }}>Time</th>
                  <th style={{ width: 70, textAlign: 'center' }}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {errors.map(row => <ErrorRow key={row.id} row={row} isMobile={false} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => p - 1)} disabled={!hasPrev}>← Prev</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => p + 1)} disabled={!hasNext}>Next →</button>
          </div>
        </div>
      )}
    </Layout>
  );
}
